import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFilters } from '../../../context/FilterContext';
import { Box, MenuItem, Select, FormControl, InputLabel, Autocomplete, TextField, Button, CircularProgress, FormHelperText, Typography } from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import MentorReviewTable from './MentorReviewTable';
import { getAllCOE, getAllSemesters } from '../../../services/adminServices/CoeServices';
import { getAllCoeLabs } from '../../../services/mentorServices/DailyPracticeServices';
import { getLabType } from '../../../services/mentorServices/MentorServices';
import { getMentorReviewsDashboard, ReviewStatus, getReviewNames, getLearnerList } from '../../../services/mentorServices/ReviewServices';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import './MentorReview.css';

const MentorReview = ({ isAdmin = false }) => {
    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);
    const isViewer = user?.role === 'coe_report_viewer';
    const userCoeId = user?.coeId || user?.coe_id || user?.coeCenter?.id || user?.coe_center_id || user?.coe || '';

    const { mentorReviewState, setMentorReviewState } = useFilters();

    const [statusFilter, setStatusFilter] = useState(() => {
        return isViewer ? 'all' : mentorReviewState.statusFilter;
    });
    const [learnerFilter, setLearnerFilter] = useState(mentorReviewState.learnerFilter);
    const [selectedLearnerId, setSelectedLearnerId] = useState(mentorReviewState.selectedLearnerId);
    const [reviewNameFilter, setReviewNameFilter] = useState(mentorReviewState.reviewNameFilter);
    const [apiReviewNames, setApiReviewNames] = useState(mentorReviewState.options.apiReviewNames || []);
    const [coeLearnersList, setCoeLearnersList] = useState(mentorReviewState.options.coeLearnersList || []);
    const [sortBy, setSortBy] = useState(mentorReviewState.sortBy);
    const [loading, setLoading] = useState(false);
    const [downloadingCSV, setDownloadingCSV] = useState(false);
    const [currentPage, setCurrentPage] = useState(mentorReviewState.currentPage);
    const [rowsPerPage, setRowsPerPage] = useState(mentorReviewState.rowsPerPage);
    const [learnerInput, setLearnerInput] = useState(mentorReviewState.learnerInput);
    const [reviewNameInput, setReviewNameInput] = useState(mentorReviewState.reviewNameInput);
    const [fromDate, setFromDate] = useState(mentorReviewState.fromDate);
    const [toDate, setToDate] = useState(mentorReviewState.toDate);

    const [selection, setSelection] = useState(() => {
        if (isViewer) {
            const baseSelection = mentorReviewState.selection || (sessionStorage.getItem('MentorReviewselection') ? JSON.parse(sessionStorage.getItem('MentorReviewselection')) : null);
            return {
                lab: baseSelection?.lab || '',
                coe: userCoeId,
                labType: baseSelection?.labType || '',
                semester: baseSelection?.semester || ''
            };
        }
        if (mentorReviewState.selection) return mentorReviewState.selection;
        const saved = sessionStorage.getItem('MentorReviewselection');
        if (saved) return JSON.parse(saved);
        return { lab: '', coe: userCoeId, labType: '', semester: '' };
    });
    const [coes, setCoe] = useState(mentorReviewState.options.coes || []);
    const [labTypes, setLabTypes] = useState(mentorReviewState.options.labTypes || []);
    const [semesters, setSemesters] = useState(mentorReviewState.options.semesters || []);
    const [labNames, setLabNames] = useState(mentorReviewState.options.labNames || []);
    const [error, setError] = useState({ lab: '', coe: '' });
    const [showTable, setShowTable] = useState(() => {
        if (mentorReviewState.showTable !== null) return mentorReviewState.showTable;
        const saved = sessionStorage.getItem('MentorReviewshowTable');
        if (saved !== null) return saved === 'true';
        return false;
    });
    const [reviewsData, setReviewsData] = useState(mentorReviewState.reviewsData);
    const [totalRecords, setTotalRecords] = useState(mentorReviewState.totalRecords);
    const [stats, setStats] = useState(mentorReviewState.stats);

    useEffect(() => {
        if (isViewer && userCoeId && selection.coe !== userCoeId) {
            setSelection(prev => ({ ...prev, coe: userCoeId }));
        }
    }, [isViewer, userCoeId, selection.coe]);

    useEffect(() => {
        if (isViewer && statusFilter !== 'all') {
            setStatusFilter('all');
        }
    }, [isViewer, statusFilter]);

    useEffect(() => {
        setMentorReviewState({
            statusFilter: isViewer ? 'all' : statusFilter, learnerFilter, selectedLearnerId, reviewNameFilter, sortBy,
            currentPage, rowsPerPage, learnerInput, reviewNameInput, fromDate, toDate,
            selection, showTable, reviewsData, totalRecords, stats,
            options: { apiReviewNames, coeLearnersList, coes, labNames, labTypes, semesters },
            hasFetched: mentorReviewState.hasFetched
        });
    }, [statusFilter, learnerFilter, selectedLearnerId, reviewNameFilter, sortBy,
        currentPage, rowsPerPage, learnerInput, reviewNameInput, fromDate, toDate,
        selection, showTable, reviewsData, totalRecords, stats, 
        apiReviewNames, coeLearnersList, coes, labNames, labTypes, semesters,
        setMentorReviewState, mentorReviewState.hasFetched, isViewer]);

    const fetchReviewNamesList = async () => {
        try {
            const response = await getReviewNames();
            const names = response?.data?.data?.reviewNames || response?.data?.reviewNames || [];
            if (Array.isArray(names)) {
                setApiReviewNames(names);
            }
        } catch (err) {
            console.error("Failed to fetch review names:", err);
        }
    };

    const fetchLearnersForLab = useCallback(async (coeId, labId) => {
        if (!coeId) {
            setCoeLearnersList([]);
            return;
        }
        try {
            const response = await getLearnerList(coeId, labId);
            const resData = response?.data?.data || response?.data;
            let list = [];
            
            if (Array.isArray(resData)) {
                list = resData;
            } else if (resData && typeof resData === 'object' && Array.isArray(resData.learners)) {
                list = resData.learners;
            }

            setCoeLearnersList(list);
        } catch (err) {
            console.error("Failed to fetch learners for lab:", err);
            setCoeLearnersList([]);
        }
    }, []);

    const getCoe = async () => {
        try {
            const response = await getAllCOE();
            setCoe(response?.data?.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || err?.error || "Failed to fetch COEs");
        }
    };

    const fetchLabTypes = async () => {
        try {
            const response = await getLabType();
            setLabTypes(response?.data?.data?.value || response?.data?.data || []);
        } catch (err) {
            console.error("Failed to fetch lab types:", err);
            setLabTypes([]);
        }
    };

    const fetchSemesters = useCallback(async (coeId) => {
        if (!coeId) {
            setSemesters([]);
            return;
        }
        try {
            const response = await getAllSemesters(coeId);
            const sortedSemesters = response?.data?.data?.sort((a, b) => a - b) || [];
            setSemesters(sortedSemesters);
        } catch (err) {
            console.error("Failed to fetch semesters:", err);
            setSemesters([]);
        }
    }, []);

    const fetchLabs = useCallback(async (coeId, labType = '', semester = '') => {
        if (!coeId) {
            setLabNames([]);
            return;
        }
        try {
            const response = await getAllCoeLabs(coeId, labType, semester);
            setLabNames(response?.data?.data || []);
        } catch (err) {
            setLabNames([]);
            toast.error(err?.response?.data?.message || err?.message || err?.error || "Failed to fetch Labs");
        }
    }, []);

    useEffect(() => {
        if (!isViewer && coes.length === 0) getCoe();
        if (labTypes.length === 0) fetchLabTypes();
        if (apiReviewNames.length === 0) fetchReviewNamesList();
        if (selection.coe) {
            if (semesters.length === 0) fetchSemesters(selection.coe);
            if (labNames.length === 0) fetchLabs(selection.coe, selection.labType, selection.semester);
        }
        if (selection.coe && coeLearnersList.length === 0) {
            fetchLearnersForLab(selection.coe, selection.lab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selection.coe && semesters.length === 0) {
            fetchSemesters(selection.coe);
        }
    }, [selection.coe, semesters.length, fetchSemesters]);

    useEffect(() => {
        sessionStorage.setItem('MentorReviewselection', JSON.stringify(selection));
    }, [selection]);

    useEffect(() => {
        sessionStorage.setItem('MentorReviewshowTable', showTable);
    }, [showTable]);

    const handleSelectionChange = (event) => {
        const { name, value } = event.target;
        setSelection(prev => {
            if (name === 'coe') {
                fetchSemesters(value);
                fetchLabs(value, prev.labType, prev.semester);
                fetchLearnersForLab(value, '');
                return { ...prev, coe: value, lab: '', semester: '' };
            }
            if (name === 'labType') {
                fetchLabs(prev.coe, value, prev.semester);
                return { ...prev, labType: value, lab: '' };
            }
            if (name === 'semester') {
                fetchLabs(prev.coe, prev.labType, value);
                return { ...prev, semester: value, lab: '' };
            }
            const updated = { ...prev, [name]: value };
            if (updated.coe) {
                fetchLearnersForLab(updated.coe, updated.lab);
            }
            return updated;
        });
        setError(prev => ({ ...prev, [name]: '' }));
        setShowTable(false);
        setLearnerFilter('all');
        setSelectedLearnerId(null);
        setReviewNameFilter('all');
        setLearnerInput('All Learners');
        setReviewNameInput('All Reviews');
        setFromDate('');
        setToDate('');
        setCurrentPage(1);
    };

    const fetchData = useCallback(async (customPage = 1, customLimit = 10, filterOverrides = {}) => {
        const currentLab = filterOverrides.lab !== undefined ? filterOverrides.lab : selection.lab;
        const { coe } = selection;

        if (!coe) {
            setError({ coe: 'Please select a COE' });
            return;
        }

        // Ensure learners list is loaded
        if (coeLearnersList.length === 0 && coe) {
            fetchLearnersForLab(coe, currentLab);
        }

        setLoading(true);
        try {
            const effectiveStatusFilter = isViewer ? 'all' : statusFilter;
            const currentStatus = isViewer ? 'all' : (filterOverrides.status !== undefined ? filterOverrides.status : effectiveStatusFilter);
            const currentLearner = filterOverrides.learner !== undefined ? filterOverrides.learner : learnerFilter;
            const currentLearnerId = filterOverrides.learnerId !== undefined ? filterOverrides.learnerId : selectedLearnerId;
            const currentReviewName = filterOverrides.reviewName !== undefined ? filterOverrides.reviewName : reviewNameFilter;
            const currentSort = filterOverrides.sort !== undefined ? filterOverrides.sort : sortBy;
            const currentFromDate = filterOverrides.fromDate !== undefined ? filterOverrides.fromDate : fromDate;
            const currentToDate = filterOverrides.toDate !== undefined ? filterOverrides.toDate : toDate;

            const apiParams = {
                page: customPage,
                limit: customLimit,
                status: currentStatus !== 'all' ? currentStatus : undefined,
                learnerId: currentLearnerId || undefined,
                coe_id: coe,
                reviewName: currentReviewName !== 'all' ? currentReviewName : undefined,
                sort: currentSort,
                lab_id: currentLab || undefined,
                lab_type: selection.labType || undefined,
                semester: selection.semester || undefined,
                from_date: currentFromDate ? currentFromDate + "T00:00:00.000Z" : undefined,
                to_date: currentToDate ? currentToDate + "T23:59:59.000Z" : undefined,
            };

            const response = await getMentorReviewsDashboard(apiParams);
            const resData = response?.data?.data || response?.data;
            let list = [];

            if (Array.isArray(resData)) {
                list = resData;
                setTotalRecords(response?.data?.pagination?.totalItems || response?.data?.totalRecords || list.length);
            } else if (resData && typeof resData === 'object') {
                list = Array.isArray(resData.reviews) ? resData.reviews : (Array.isArray(resData.data) ? resData.data : (Array.isArray(resData.list) ? resData.list : []));
                setTotalRecords(resData.pagination?.totalItems || resData.totalRecords || resData.totalCount || list.length);
            }

            setReviewsData(list);
            setShowTable(true);

            if (resData?.summary) {
                setStats({
                    totalLearners: resData.summary.totalLearners || 0,
                    totalReviews: resData.summary.totalReviews || 0,
                    completedReviews: resData.summary.totalCompletedReviews || 0,
                    verificationPending: resData.summary.totalVerificationPending || 0,
                    reviewPending: resData.summary.totalReviewPending ?? resData.summary.reviewPending,
                    evaluationPending: resData.summary.totalEvaluationPending ?? resData.summary.evaluationPending,
                    evaluationFailed: resData.summary.totalEvaluationFailed ?? resData.summary.evaluationFailed,
                    lcPending: resData.summary.totalLcPending ?? resData.summary.lcPending,
                });
            } else if (resData?.stats) {
                setStats({
                    totalLearners: resData.stats.totalLearners || 0,
                    totalReviews: resData.stats.totalReviews || 0,
                    completedReviews: resData.stats.totalCompletedReviews || resData.stats.completedReviews || 0,
                    verificationPending: resData.stats.totalVerificationPending || resData.stats.pendingReviews || 0,
                    reviewPending: resData.stats.totalReviewPending ?? resData.stats.reviewPending,
                    evaluationPending: resData.stats.totalEvaluationPending ?? resData.stats.evaluationPending,
                    evaluationFailed: resData.stats.totalEvaluationFailed ?? resData.stats.evaluationFailed,
                    lcPending: resData.stats.totalLcPending ?? resData.stats.lcPending,
                });
            } else if (resData && typeof resData === 'object' && !Array.isArray(resData) && (resData.totalLearners !== undefined || resData.totalReviews !== undefined)) {
                setStats({
                    totalLearners: resData.totalLearners || 0,
                    totalReviews: resData.totalReviews || 0,
                    completedReviews: resData.totalCompletedReviews || resData.completedReviews || 0,
                    verificationPending: resData.totalVerificationPending || resData.pendingReviews || 0,
                    reviewPending: resData.totalReviewPending ?? resData.reviewPending,
                    evaluationPending: resData.totalEvaluationPending ?? resData.evaluationPending,
                    evaluationFailed: resData.totalEvaluationFailed ?? resData.evaluationFailed,
                    lcPending: resData.totalLcPending ?? resData.lcPending,
                });
            } else {
                const totalRev = resData?.pagination?.totalItems || resData?.totalRecords || list.length;
                const completedRev = list.filter(item => (item?.status || item?.aiGradingStatus?.coding || '').toLowerCase() === 'completed').length;
                const verifPendRev = list.filter(item => (item?.status || item?.aiGradingStatus?.coding || '').toLowerCase() === 'verification_pending' || (item?.status || '').toLowerCase() === 'pending').length;
                const reviewRev = list.filter(item => (item?.status || '').toLowerCase() === 'review_pending').length;
                const evalPendRev = list.filter(item => (item?.status || '').toLowerCase() === 'evaluation_pending').length;
                const evalFailRev = list.filter(item => (item?.status || '').toLowerCase() === 'evaluation_failed').length;
                const lcRev = list.filter(item => (item?.status || '').toLowerCase() === 'lc_pending').length;
                const totalLearn = new Set(list.map(item => item?.learnerName || item?.learner_name || item?.Learner?.name || item?.learner?.name || item?.name)).size;
                setStats({
                    totalLearners: totalLearn,
                    totalReviews: totalRev,
                    completedReviews: completedRev,
                    verificationPending: verifPendRev,
                    reviewPending: reviewRev,
                    evaluationPending: evalPendRev,
                    evaluationFailed: evalFailRev,
                    lcPending: lcRev
                });
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || err?.error || "Failed to fetch reviews");
            setReviewsData([]);
            setTotalRecords(0);
            setShowTable(false);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection, statusFilter, learnerFilter, reviewNameFilter, sortBy, fromDate, toDate, isViewer]);

    useEffect(() => {
        // Initial fetch if table was previously shown
        const user = JSON.parse(localStorage.getItem('user')) || {};
        const isViewer = user.role === 'coe_report_viewer';

        if ((showTable || isViewer) && selection.coe && !mentorReviewState.hasFetched) {
            setMentorReviewState(prev => ({ ...prev, hasFetched: true }));
            fetchData(currentPage, rowsPerPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownloadAllCSV = async () => {
        const { lab, coe } = selection;
        if (!coe) {
            toast.info("Please select a COE first.");
            return;
        }

        const countToFetch = totalRecords > 0 ? totalRecords : (filteredData?.length || 10);
        if (countToFetch === 0) {
            toast.info("No Learner Review data available to download.");
            return;
        }

        setDownloadingCSV(true);
        try {
            const effectiveStatus = isViewer ? 'all' : statusFilter;
            const apiParams = {
                page: 1,
                limit: countToFetch,
                status: effectiveStatus !== 'all' ? effectiveStatus : undefined,
                learnerId: selectedLearnerId || undefined,
                coe_id: coe,
                reviewName: reviewNameFilter !== 'all' ? reviewNameFilter : undefined,
                sort: sortBy,
                lab_id: lab || undefined,
                lab_type: selection.labType || undefined,
                semester: selection.semester || undefined,
                from_date: fromDate ? fromDate + "T00:00:00.000Z" : undefined,
                to_date: toDate ? toDate + "T23:59:59.000Z" : undefined,
            };

            const response = await getMentorReviewsDashboard(apiParams);
            const resData = response?.data?.data || response?.data;
            let list = [];

            if (Array.isArray(resData)) {
                list = resData;
            } else if (resData && typeof resData === 'object') {
                list = Array.isArray(resData.reviews) ? resData.reviews : (Array.isArray(resData.data) ? resData.data : (Array.isArray(resData.list) ? resData.list : []));
            }

            if (!list || list.length === 0) {
                toast.info("No Learner Review data available to download.");
                return;
            }

            const formattedData = list.map((item, index) => {
                const learnerName = item.learnerName || item.learner_name || item?.Learner?.name || item?.learner?.name || item?.name || "-";
                const rollNumber = item.rollNo || item.rollNumber || item.roll_number || item?.Learner?.rollNumber || item?.Learner?.roll_number || item?.Learner?.rollNo || item?.Learner?.rollNum || item?.learner?.roll_num || item?.roll_num || item?.rollNum || "-";
                const emailId = item.emailId || item.email_id || item?.Learner?.emailId || item?.Learner?.email || item?.Learner?.email_id || item?.learner?.email || item?.email || "-";
                const labName = item.labName || item.lab_name || item?.Learner?.labName || item?.Learner?.lab_name || item?.learner?.lab_name || item?.learner?.labName || "-";
                const reviewName = item.reviewName || item.reviewTitle || item.review_name || "-";
                const subDateRaw = item.submissionDate || item.submission_date || item.created_at || item.submittedDate || item.submitted_date;
                const subDate = subDateRaw ? new Date(subDateRaw).toLocaleString('en-US') : "-";

                const tScore = item.techExcellence !== undefined && item.techExcellence !== null ? item.techExcellence : "-";
                const lScore = item.learnability !== undefined && item.learnability !== null ? item.learnability : "-";
                const cScore = item.communicability !== undefined && item.communicability !== null ? item.communicability : "-";

                let totalScoreStr = "-";
                if (tScore !== "-" || lScore !== "-" || cScore !== "-") {
                    const t = tScore !== "-" ? Number(tScore) : 0;
                    const l = lScore !== "-" ? Number(lScore) : 0;
                    const c = cScore !== "-" ? Number(cScore) : 0;
                    totalScoreStr = (t + l + c).toFixed(2);
                } else if (item.totalScore !== undefined && item.totalScore !== null) {
                    totalScoreStr = Number(item.totalScore).toFixed(2);
                } else if (item.total_score !== undefined && item.total_score !== null) {
                    totalScoreStr = Number(item.total_score).toFixed(2);
                }

                const status = (item.status || "PENDING").replace(/_/g, ' ');

                return {
                    "Sr.No": index + 1,
                    "Roll Number": rollNumber,
                    "Learner Name": learnerName,
                    "Email": emailId,
                    "Lab Name": labName,
                    "Review Name": reviewName,
                    "Submission Date": subDate,
                    "Techability  (T)": tScore,
                    "Learnability (L)": lScore,
                    "Communicability (C)": cScore,
                    "Total Score": totalScoreStr,
                    "Status": status
                };
            });

            const csvContent = Papa.unparse(formattedData);
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const cleanName = (reviewNameFilter && reviewNameFilter !== 'all')
                ? reviewNameFilter.replace(/[^a-zA-Z0-9]/g, '_')
                : 'All_Reviews';
            link.setAttribute("download", `Learner_Review_Scores_${cleanName}_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success("CSV file downloaded successfully!");
        } catch (err) {
            console.error("Failed to download full scores CSV:", err);
            toast.error(err?.response?.data?.message || err?.message || "Failed to download scores CSV.");
        } finally {
            setDownloadingCSV(false);
        }
    };

    const isActionDisabled = !selection.coe;

    const learnerOptions = useMemo(() => {
        const map = new Map();

        coeLearnersList.forEach(item => {
            const l = item?.learner || item;
            const name = l?.name || l?.fullName || l?.learner_name;
            const id = l?.learner_id || l?.learnerId || l?.id || item?.learner_id || item?.id;
            const rollNum = l?.roll_num || l?.rollNum || l?.roll_no || l?.rollNo || item?.roll_num || '';

            if (name) {
                const label = rollNum ? `${name} - ${rollNum}` : name;
                const key = id ? String(id) : `${name.toLowerCase()}_${rollNum.toLowerCase()}`;
                map.set(key, {
                    label: label,
                    value: key,
                    learnerId: id || null,
                    rawName: name,
                    rollNum: rollNum
                });
            }
        });

        reviewsData.forEach(item => {
            const l = item?.learner || item?.Learner;
            const name = item?.learnerName || item?.learner_name || l?.name || item?.name;
            const id = item?.learnerId || item?.learner_id || l?.id || l?.learner_id;
            const rollNum = item?.roll_num || item?.rollNum || l?.roll_num || '';

            if (name) {
                const key = id ? String(id) : `${name.toLowerCase()}_${rollNum.toLowerCase()}`;
                if (!map.has(key)) {
                    const label = rollNum ? `${name} - ${rollNum}` : name;
                    map.set(key, {
                        label: label,
                        value: key,
                        learnerId: id || null,
                        rawName: name,
                        rollNum: rollNum
                    });
                }
            }
        });

        const sortedMapOptions = Array.from(map.values()).sort((a, b) => {
            return (a.label || '').localeCompare((b.label || ''), undefined, { numeric: true, sensitivity: 'base' });
        });
        const options = [{ label: 'All Learners', value: 'all', learnerId: null, rawName: 'all', rollNum: '' }];
        sortedMapOptions.forEach(opt => {
            options.push(opt);
        });
        return options;
    }, [coeLearnersList, reviewsData]);

    const reviewNameOptions = useMemo(() => {
        const names = new Set(apiReviewNames);
        reviewsData.forEach(item => {
            const rName = item?.reviewName || item?.reviewTitle || item?.review_name || item?.review?.name;
            if (rName) names.add(rName);
        });
        const sortedNames = Array.from(names).sort((a, b) => {
            return (a || '').localeCompare((b || ''), undefined, { numeric: true, sensitivity: 'base' });
        });
        const options = [{ label: 'All Reviews', value: 'all' }];
        sortedNames.forEach(name => {
            options.push({ label: name, value: name });
        });
        return options;
    }, [apiReviewNames, reviewsData]);

    const filteredData = useMemo(() => {
        const effectiveStatus = isViewer ? 'all' : statusFilter;
        return reviewsData.filter(item => {
            const status = (item?.status || '').toUpperCase();
            if (effectiveStatus !== 'all' && status !== effectiveStatus.toUpperCase()) {
                return false;
            }
            if (learnerFilter !== 'all') {
                const itemLearnerId = item?.learnerId || item?.learner_id || item?.Learner?.id || item?.learner?.id;
                if (selectedLearnerId && itemLearnerId) {
                    if (String(itemLearnerId) !== String(selectedLearnerId)) {
                        return false;
                    }
                } else {
                    const name = (item?.learnerName || item?.learner_name || item?.Learner?.name || item?.learner?.name || item?.name || '').toLowerCase();
                    const selectedOpt = learnerOptions.find(o => o.value === learnerFilter);
                    const targetName = (selectedOpt?.rawName || learnerFilter).toLowerCase();
                    if (!name.includes(targetName)) {
                        return false;
                    }
                }
            }
            if (reviewNameFilter !== 'all') {
                const rName = (item?.reviewName || item?.reviewTitle || item?.review_name || item?.review?.name || '').toLowerCase();
                if (!rName.includes(reviewNameFilter.toLowerCase())) {
                    return false;
                }
            }
            return true;
        }).sort((a, b) => {
            const dateA = new Date(a?.submissionDate || a?.submission_date || a?.created_at || 0);
            const dateB = new Date(b?.submissionDate || b?.submission_date || b?.created_at || 0);

            const getScore = (item) => {
                if (item?.totalScore !== undefined) return item.totalScore;
                if (item?.total_score !== undefined) return item.total_score;
                if (item?.score !== undefined) return item.score;
                const t = item?.techExcellence !== undefined && item?.techExcellence !== null ? Number(item.techExcellence) : 0;
                const l = item?.learnability !== undefined && item?.learnability !== null ? Number(item.learnability) : 0;
                const c = item?.communicability !== undefined && item?.communicability !== null ? Number(item.communicability) : 0;
                return t + l + c;
            };

            const scoreA = getScore(a);
            const scoreB = getScore(b);

            if (sortBy === 'latest') {
                return dateB - dateA;
            } else if (sortBy === 'oldest') {
                return dateA - dateB;
            } else if (sortBy === 'highest_score' || sortBy === 'score_high') {
                return (scoreB || 0) - (scoreA || 0);
            } else if (sortBy === 'lowest_score' || sortBy === 'score_low') {
                return (scoreA || 0) - (scoreB || 0);
            }
            return 0;
        });
    }, [reviewsData, statusFilter, learnerFilter, selectedLearnerId, reviewNameFilter, sortBy, learnerOptions, isViewer]);

    const formatCount = (count) => count < 10 ? `0${count}` : count;
    const formatCountOrDash = (count) => (count === undefined || count === null) ? '-' : formatCount(count);

    const inProgressCount = useMemo(() => {
        const parseCount = (val) => {
            if (val === null || val === undefined || val === '-') return 0;
            const num = Number(val);
            return isNaN(num) ? 0 : num;
        };
        return (
            parseCount(stats.verificationPending) +
            parseCount(stats.reviewPending) +
            parseCount(stats.evaluationPending) +
            parseCount(stats.evaluationFailed) +
            parseCount(stats.lcPending)
        );
    }, [stats]);

    const selectStyle = {
        backgroundColor: '#fff',
        borderRadius: '8px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e0e0e0',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#b0b0b0',
        },
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
    };

    return (
        <Box className="learner-review-container">
            <Box className="learner-review-filters-container" sx={{ mb: 3, justifyContent: 'flex-start !important', gap: 2, flexWrap: 'wrap' }}>
                {!isViewer && (
                    <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: { xs: '100%', sm: 180 }, maxWidth: { md: 280 } }} error={!!error.coe}>
                        <InputLabel id="coe-select-label">COE*</InputLabel>
                        <Select
                            labelId="coe-select-label"
                            id="coe-select"
                            name="coe"
                            value={selection.coe}
                            onChange={handleSelectionChange}
                            label="COE"
                            sx={selectStyle}
                        >
                            {coes.map((coe) => (
                                <MenuItem key={coe.id} value={coe.id}>
                                    {coe.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.coe && <FormHelperText>{error.coe}</FormHelperText>}
                    </FormControl>
                )}

                {!isViewer && (
                    <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' }, minWidth: { xs: '100%', sm: 160 }, maxWidth: { md: 240 } }}>
                        <InputLabel id="lab-type-select-label" shrink>Lab Type</InputLabel>
                        <Select
                            labelId="lab-type-select-label"
                            id="lab-type-select"
                            name="labType"
                            value={selection.labType || ''}
                            onChange={handleSelectionChange}
                            label="Lab Type"
                            notched
                            displayEmpty
                            renderValue={(selected) => selected || "All Lab Types"}
                            sx={selectStyle}
                        >
                            <MenuItem value="">All Lab Types</MenuItem>
                            {labTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' }, minWidth: { xs: '100%', sm: 160 }, maxWidth: { md: 240 } }}>
                    <InputLabel id="semester-select-label" shrink>Semester</InputLabel>
                    <Select
                        labelId="semester-select-label"
                        id="semester-select"
                        name="semester"
                        value={selection.semester || ''}
                        onChange={handleSelectionChange}
                        label="Semester"
                        notched
                        displayEmpty
                        renderValue={(selected) => (selected !== '' && selected !== null && selected !== undefined) ? selected : "All Semesters"}
                        sx={selectStyle}
                        disabled={!selection.coe}
                    >
                        <MenuItem value="">All Semesters</MenuItem>
                        {semesters.filter(sem => sem != null).map((sem) => (
                            <MenuItem key={sem} value={sem}>
                                {sem}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {(!isAdmin || isViewer) && (
                    <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: { xs: '100%', sm: 180 }, maxWidth: { md: 280 } }} error={!!error.lab}>
                        <InputLabel id="lab-select-label" shrink>Lab Name</InputLabel>
                        <Select
                            labelId="lab-select-label"
                            id="lab-select"
                            name="lab"
                            value={selection.lab || ''}
                            onChange={handleSelectionChange}
                            label="Lab Name"
                            notched
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) return "All Labs";
                                const found = labNames.find((lab) => lab.id === selected);
                                return found ? found.lab_name : selected;
                            }}
                            sx={selectStyle}
                        >
                            <MenuItem value="">All Labs</MenuItem>
                            {labNames.map((lab) => (
                                <MenuItem key={lab.id} value={lab.id}>
                                    {lab.lab_name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.lab && <FormHelperText>{error.lab}</FormHelperText>}
                    </FormControl>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => fetchData(1, 10)}
                    sx={{ borderRadius: '8px', textTransform: 'none', height: '40px', px: 4, fontWeight: 600, width: { xs: '100%', sm: 'auto' }, alignSelf: { sm: 'center' } }}
                    disabled={loading || isActionDisabled}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'VIEW'}
                </Button>
            </Box>

            {!showTable || (loading && reviewsData.length === 0) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '350px', width: '100%', mt: 4 }}>
                    {loading ? (
                        <CircularProgress size={60} />
                    ) : (
                        <>
                            <SchoolOutlinedIcon sx={{ fontSize: 100, color: '#e0e0e0', mb: 2 }} />
                            <Typography sx={{ color: '#5f6368', fontSize: '18px', fontWeight: 400, textAlign: 'center' }}>
                                Learner details will appear here after selecting COE.
                            </Typography>
                        </>
                    )}
                </Box>
            ) : (
                <>
                    <Box className="mentor-review-stats-grid">
                        <Box className="learner-review-stat-card">
                            <span className="learner-review-stat-title">Total Learners</span>
                            <span className="learner-review-stat-value">{formatCount(stats.totalLearners)}</span>
                        </Box>
                        <Box className="learner-review-stat-card">
                            <span className="learner-review-stat-title">Total Reviews</span>
                            <span className="learner-review-stat-value">{formatCount(stats.totalReviews)}</span>
                        </Box>
                        <Box className="learner-review-stat-card completed">
                            <span className="learner-review-stat-title">Completed </span>
                            <span className="learner-review-stat-value">{formatCount(stats.completedReviews)}</span>
                        </Box>
                        {isViewer ? (
                            <Box className="learner-review-stat-card pending in-progress">
                                <span className="learner-review-stat-title">Total In Progress </span>
                                <span className="learner-review-stat-value">{formatCount(inProgressCount)}</span>
                            </Box>
                        ) : (
                            <>
                                <Box className="learner-review-stat-card pending">
                                    <span className="learner-review-stat-title">Verification Pending </span>
                                    <span className="learner-review-stat-value">{formatCount(stats.verificationPending)}</span>
                                </Box>
                                <Box className="learner-review-stat-card review-pending">
                                    <span className="learner-review-stat-title">Review Pending </span>
                                    <span className="learner-review-stat-value">{formatCountOrDash(stats.reviewPending)}</span>
                                </Box>
                                <Box className="learner-review-stat-card evaluation-pending">
                                    <span className="learner-review-stat-title">Evaluation Pending </span>
                                    <span className="learner-review-stat-value">{formatCountOrDash(stats.evaluationPending)}</span>
                                </Box>
                                <Box className="learner-review-stat-card evaluation-failed">
                                    <span className="learner-review-stat-title">Evaluation Failed </span>
                                    <span className="learner-review-stat-value">{formatCountOrDash(stats.evaluationFailed)}</span>
                                </Box>
                                <Box className="learner-review-stat-card lc-pending">
                                    <span className="learner-review-stat-title">LC Pending </span>
                                    <span className="learner-review-stat-value">{formatCountOrDash(stats.lcPending)}</span>
                                </Box>
                            </>
                        )}
                    </Box>

                    <Box className="learner-review-filters-container" sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2, width: '100%' }}>
                            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>Learners</span>
                                <Autocomplete
                                    size="small"
                                    options={learnerOptions}
                                    getOptionLabel={(option) => option?.label || ""}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                    value={learnerInput === '' ? null : (learnerOptions.find(opt => opt.value === learnerFilter) || learnerOptions[0] || null)}
                                    inputValue={learnerInput}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason === 'input') {
                                            setLearnerInput(newInputValue);
                                        } else if (reason === 'clear') {
                                            setLearnerInput('');
                                            setLearnerFilter('all');
                                            setSelectedLearnerId(null);
                                            setCurrentPage(1);
                                        } else if (reason === 'reset') {
                                            if (newInputValue && newInputValue !== 'All Learners') {
                                                setLearnerInput(newInputValue);
                                            }
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!learnerInput || !learnerInput.trim() || learnerFilter === 'all') {
                                            setLearnerFilter('all');
                                            setSelectedLearnerId(null);
                                            setLearnerInput('All Learners');
                                        } else {
                                            const current = learnerOptions.find(opt => opt.value === learnerFilter);
                                            setLearnerInput(current?.label || 'All Learners');
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        if (!newValue) {
                                            setLearnerFilter('all');
                                            setSelectedLearnerId(null);
                                            setLearnerInput('');
                                            setCurrentPage(1);
                                            return;
                                        }
                                        const val = newValue.value;
                                        const lId = newValue.learnerId || null;
                                        setLearnerFilter(val);
                                        setSelectedLearnerId(lId);
                                        setLearnerInput(val === 'all' ? 'All Learners' : (newValue.label || ''));
                                        setCurrentPage(1);
                                        fetchData(1, rowsPerPage, { learner: val, learnerId: lId });
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Search Learner" sx={selectStyle} />
                                    )}
                                    disableClearable={false}
                                />
                            </FormControl>

                            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>Review Name</span>
                                <Autocomplete
                                    size="small"
                                    options={reviewNameOptions}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : (option?.label || "")}
                                    isOptionEqualToValue={(option, value) => {
                                        const optVal = typeof option === 'string' ? option : option?.value;
                                        const valVal = typeof value === 'string' ? value : value?.value;
                                        return optVal === valVal;
                                    }}
                                    value={reviewNameInput === '' ? null : (reviewNameOptions.find(opt => opt.value === reviewNameFilter) || (reviewNameFilter !== 'all' ? (typeof reviewNameFilter === 'string' ? { label: reviewNameFilter, value: reviewNameFilter } : null) : reviewNameOptions[0]) || null)}
                                    inputValue={reviewNameInput}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason === 'input') {
                                            setReviewNameInput(newInputValue);
                                        } else if (reason === 'clear') {
                                            setReviewNameInput('');
                                            setReviewNameFilter('all');
                                            setCurrentPage(1);
                                        } else if (reason === 'reset') {
                                            if (newInputValue && newInputValue !== 'All Reviews') {
                                                setReviewNameInput(newInputValue);
                                            }
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!reviewNameInput || !reviewNameInput.trim() || reviewNameFilter === 'all') {
                                            setReviewNameFilter('all');
                                            setReviewNameInput('All Reviews');
                                        } else {
                                            const current = reviewNameOptions.find(opt => opt.value === reviewNameFilter);
                                            setReviewNameInput(typeof current === 'string' ? current : (current?.label || 'All Reviews'));
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        if (!newValue) {
                                            setReviewNameFilter('all');
                                            setReviewNameInput('');
                                            setCurrentPage(1);
                                            return;
                                        }
                                        const val = typeof newValue === 'string' ? newValue : (newValue.value || 'all');
                                        const label = typeof newValue === 'string' ? newValue : (newValue.label || val);
                                        setReviewNameFilter(val);
                                        setReviewNameInput(val === 'all' ? 'All Reviews' : label);
                                        setCurrentPage(1);
                                        fetchData(1, rowsPerPage, { reviewName: val });
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Search Review Name" sx={selectStyle} />
                                    )}
                                />
                            </FormControl>

                            {isAdmin && !isViewer && (
                                <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>Lab Name</span>
                                    <Select
                                        value={selection.lab || 'all'}
                                        onChange={(e) => {
                                            const val = e.target.value === 'all' ? '' : e.target.value;
                                            setSelection(prev => {
                                                const updated = { ...prev, lab: val };
                                                if (updated.coe) {
                                                    fetchLearnersForLab(updated.coe, updated.lab);
                                                }
                                                return updated;
                                            });
                                            setCurrentPage(1);
                                            fetchData(1, rowsPerPage, { lab: val });
                                        }}
                                        sx={selectStyle}
                                    >
                                        <MenuItem value="all">All Labs</MenuItem>
                                        {labNames.map((lab) => (
                                            <MenuItem key={lab.id} value={lab.id}>
                                                {lab.lab_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {!isViewer && (
                                <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>Status</span>
                                    <Select
                                        value={statusFilter}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setStatusFilter(val);
                                            setCurrentPage(1);
                                            fetchData(1, rowsPerPage, { status: val });
                                        }}
                                        sx={selectStyle}
                                    >
                                        <MenuItem value="all">All Statuses</MenuItem>
                                        <MenuItem value={ReviewStatus.REVIEW_PENDING}>Review Pending</MenuItem>
                                        <MenuItem value={ReviewStatus.EVALUATION_PENDING}>Evaluation Pending</MenuItem>
                                        <MenuItem value={ReviewStatus.EVALUATION_FAILED}>Evaluation Failed</MenuItem>
                                        <MenuItem value={ReviewStatus.LC_PENDING}>LC Pending</MenuItem>
                                        <MenuItem value={ReviewStatus.VERIFICATION_PENDING}>Verification Pending</MenuItem>
                                        <MenuItem value={ReviewStatus.COMPLETED}>Completed</MenuItem>
                                        <MenuItem value={ReviewStatus.EMAIL_QUEUED}>Email Queued</MenuItem>
                                        <MenuItem value={ReviewStatus.EMAIL_SENDING}>Email Sending</MenuItem>
                                        <MenuItem value={ReviewStatus.EMAIL_FAILED}>Email Failed</MenuItem>
                                        <MenuItem value={ReviewStatus.TLC_PUBLISHED}>TLC Published</MenuItem>
                                    </Select>
                                </FormControl>
                            )}

                            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '140px' } }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>From Date</span>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={fromDate}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setFromDate(val);
                                        if (val && toDate && new Date(val) > new Date(toDate)) {
                                            if (new Date(val).getFullYear() > 999) {
                                                toast.error("From Date cannot be later than To Date");
                                            }
                                            return;
                                        }
                                        setCurrentPage(1);
                                        fetchData(1, rowsPerPage, { fromDate: val });
                                    }}
                                    sx={selectStyle}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </FormControl>

                            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '140px' } }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>To Date</span>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={toDate}
                                    inputProps={{ min: fromDate }}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setToDate(val);
                                        if (val && fromDate && new Date(val) < new Date(fromDate)) {
                                            if (new Date(val).getFullYear() > 999) {
                                                toast.error("To Date cannot be earlier than From Date");
                                            }
                                            return;
                                        }
                                        setCurrentPage(1);
                                        fetchData(1, rowsPerPage, { toDate: val });
                                    }}
                                    sx={selectStyle}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </FormControl>
                    </Box>

                    <Box className="dashboard-box-container" sx={{ width: '100%', mt: 0 }}>
                        <MentorReviewTable
                            data={filteredData}
                            loading={loading}
                            totalCount={totalRecords}
                            currentPage={currentPage}
                            selectedReviewName={reviewNameFilter}
                            onPageChange={(page, limit) => {
                                setCurrentPage(page);
                                fetchData(page, limit);
                            }}
                            onRowsPerPageChange={(page, limit) => {
                                setCurrentPage(1);
                                setRowsPerPage(limit);
                                fetchData(1, limit);
                            }}
                            onRefresh={() => fetchData(currentPage, rowsPerPage)}
                            onDownloadCSV={handleDownloadAllCSV}
                            downloadingCSV={downloadingCSV}
                            isAdmin={isAdmin}
                            sortFilterNode={
                                <FormControl size="small" sx={{ minWidth: '160px' }}>
                                    <Select
                                        value={sortBy}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSortBy(val);
                                            setCurrentPage(1);
                                            fetchData(1, rowsPerPage, { sort: val });
                                        }}
                                        sx={selectStyle}
                                        displayEmpty
                                    >
                                        <MenuItem value="latest">Latest Submissions</MenuItem>
                                        <MenuItem value="oldest">Oldest Submissions</MenuItem>
                                    </Select>
                                </FormControl>
                            }
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default MentorReview;

